'use client'

import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import type React from 'react'
import AppLink from '@/components/elementary/AppLink'
import { MobileLocaleSwitcher } from '@/components/elementary/MobileLocaleSwitcher'
import { CTAButtons } from '@/components/elementary/navbar/mobile/CTAButtons'
import { NavbarColumnGroup } from '@/components/elementary/navbar/NavbarColumnGroup'
import { NavbarMenuLink } from '@/components/elementary/navbar/NavbarMenuLink'
import { CaretRight } from '@/components/icons/CaretRight'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import { usePathname } from '@/i18n/navigation'
import { ROOT_BLOG_PAGE_PATH } from '@/lib/constants'
import { cn } from '@/lib/styles'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'
import type { AppLocale } from '@/types/general'
import type { LocaleLink } from '@/types/i18n.types'
import { getBlogCategoryLink, getBlogLink } from '@/utils/links'
import styles from './MobileMenu.module.css'

interface MobileMenuProps {
	navbarData: Data.ContentType<'api::navbar.navbar'>
	categories: Data.ContentType<'api::blog-category.blog-category'>[]
	isMobileMenuOpen: boolean
	resetMobileMenu: () => void
	activeNavMenuContent: Data.Component<'layout-utilities.navbar-item'> | null
	setActiveNavMenuContent: (item: Data.Component<'layout-utilities.navbar-item'> | null) => void
	isOpenDropDown: boolean
	setIsOpenDropDown: (isOpen: boolean) => void
	locale: AppLocale
	localeLinks?: LocaleLink[]
	ref?: React.RefObject<HTMLElement>
}

export const MobileMenu = ({
	navbarData,
	categories,
	isMobileMenuOpen,
	resetMobileMenu,
	activeNavMenuContent,
	setActiveNavMenuContent,
	isOpenDropDown,
	setIsOpenDropDown,
	locale,
	localeLinks,
	ref,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Easy to follow, could potentially split up if we see need
}: MobileMenuProps) => {
	const { items, dropdown, login, buttons } = navbarData
	const t = useTranslations('MobileMenu')

	const isBlogPage = usePathname().startsWith(ROOT_BLOG_PAGE_PATH)
	const showBlogCategories = isBlogPage && !isOpenDropDown && !activeNavMenuContent

	const showDropdownMenuContent = dropdown && isOpenDropDown
	const showNavMenuContent = (activeNavMenuContent?.columns?.length ?? 0) > 0

	function hasNavMenuContent(item: Data.Component<'layout-utilities.navbar-item'>) {
		return item.columns && item.columns.length > 0
	}

	return (
		<nav
			ref={ref}
			className={cn(
				'fixed top-16 z-50 flex flex-col px-6 bg-neutral-light-100 overflow-y-scroll max-h-screen-minus-16 min-h-screen-minus-16 w-full lg:hidden divide-y divide-neutral-light-500',
				'transition-transform duration-500 ease-in-out',
				isMobileMenuOpen ? 'translate-x-0 visible' : 'translate-x-full',
			)}
		>
			{!activeNavMenuContent && !isOpenDropDown ? (
				<ul className="flex flex-col">
					{/* Main navigation */}
					{items?.map((item) =>
						!hasNavMenuContent(item) && item.href ? (
							<li key={item.id}>
								<AppLink
									href={item.href}
									openExternalInNewTab={item?.newTab ?? false}
									className="flex items-center justify-between py-8 border-b border-neutral-light-500 w-full"
									onClick={resetMobileMenu}
								>
									<Paragraph
										fontSize="displaySmall"
										className={cn('text-inherit', styles['menu-link'])}
									>
										{item.label}
									</Paragraph>
								</AppLink>
							</li>
						) : (
							<li key={item.id}>
								<Button
									onClick={() => setActiveNavMenuContent(item)}
									variant="primaryLink"
									size="none"
									className="flex items-center justify-between py-8 border-b border-neutral-light-500 w-full"
								>
									<Paragraph
										fontSize="displaySmall"
										className={cn('text-inherit', styles['menu-link'])}
									>
										{item.label}
									</Paragraph>
									<CaretRight />
								</Button>
							</li>
						),
					)}

					{/* Dropdown menu trigger */}
					{!isOpenDropDown && dropdown ? (
						<li>
							<Button
								onClick={() => setIsOpenDropDown(true)}
								variant="primaryLink"
								size="none"
								className="flex items-center justify-between py-8 w-full"
							>
								<Paragraph
									fontSize="displaySmall"
									className={cn('text-inherit', styles['menu-link'])}
								>
									{dropdown?.label}
								</Paragraph>
								<CaretRight />
							</Button>
						</li>
					) : null}

					{/* Blog navigation */}
					{showBlogCategories ? (
						<>
							<li>
								<AppLink
									href={getBlogLink()}
									className="flex items-center justify-between py-8  border-t border-b-0 border-neutral-light-500"
									onClick={resetMobileMenu}
								>
									<Paragraph
										fontSize="displaySmall"
										className={cn('text-inherit', styles['menu-link'])}
									>
										{t('blogTitle')}
									</Paragraph>
								</AppLink>
							</li>

							{categories.map((category) => (
								<li key={category.id}>
									<AppLink
										href={getBlogCategoryLink(category.slug)}
										className="flex items-center justify-between py-8 border-t border-b-0 border-neutral-light-500"
										onClick={resetMobileMenu}
									>
										<Paragraph
											fontSize="displaySmall"
											className={cn('text-inherit', styles['menu-link'])}
										>
											{category.name}
										</Paragraph>
									</AppLink>
								</li>
							))}
						</>
					) : null}
				</ul>
			) : (
				<>
					{/* Main navigation item content */}
					{showNavMenuContent ? (
						<div className="divide-y divide-neutral-light-500">
							{activeNavMenuContent?.columns?.map((column) => (
								<div className="divide-y divide-neutral-light-500" key={`menuItem-${column.id}`}>
									{column.groups?.map((group, groupIndex) => (
										<NavbarColumnGroup
											key={`categoryMobileCard-${group.id}`}
											group={group}
											columnTitle={groupIndex === 0 ? column.title : null}
											testId={NAVBAR_TEST_IDS.CATEGORY_COLUMN_MOBILE}
											className="py-8"
											onLinkClick={resetMobileMenu}
										/>
									))}
								</div>
							))}
						</div>
					) : null}

					{/* Dropdown menu content */}
					{showDropdownMenuContent ? (
						<ul className="divide-y divide-neutral-light-500">
							{dropdown.links?.map((link) => (
								<li key={link.id} className="py-8">
									<NavbarMenuLink link={link} isDropDownItem onClick={resetMobileMenu} />
								</li>
							))}
						</ul>
					) : null}
				</>
			)}

			{/* Login and call to action buttons */}
			<CTAButtons
				login={login}
				buttons={buttons}
				dropdown={
					(activeNavMenuContent || isOpenDropDown) && !showDropdownMenuContent ? dropdown : null
				}
				navContext={!activeNavMenuContent && !isOpenDropDown ? 'menu-items' : 'menu-content'}
				onDropdownClick={() => {
					setIsOpenDropDown(true)
					setActiveNavMenuContent(null)
				}}
			/>

			{/* Locale Switcher */}
			{localeLinks && localeLinks.length > 1 && !activeNavMenuContent && !isOpenDropDown && (
				<div className="py-8">
					<MobileLocaleSwitcher currentLocale={locale} localeLinks={localeLinks} />
				</div>
			)}
		</nav>
	)
}
